<!--
  The catalogue's atom. Poster, title, year — and, when this browser has started
  it, a hairline of progress across the bottom of the art.

  IT IS A DIV WITH A LINK IN IT, NOT A LINK. A control has to be able to sit on
  top of the artwork — today the trailer's mute button — and a `<button>` inside
  an `<a>` is invalid HTML that browsers resolve by guessing. So the anchor
  covers everything that navigates and any control is its sibling, drawn over
  the frame.

  There is deliberately NO save/bookmark overlay on a tile (the owner called it
  tacky, 2026-08-16, and he was right): saving belongs to the title page, which
  every tile already leads to. If a corner control ever comes back, it must be
  `data-dpad-skip` — see dpad.ts for why a tile is ONE stop on a remote.
-->
<script lang="ts">
  import type { CatalogItem } from '$types';
  import Art from './Art.svelte';
  import CertChip from './CertChip.svelte';
  import Icon from './Icon.svelte';
  import AwardMark from './AwardMark.svelte';
  import MuteButton from './MuteButton.svelte';
  import RankNumeral from './RankNumeral.svelte';
  import Skeleton from './Skeleton.svelte';
  import { AWARDS, type AwardId } from './awards';
  import { certOf } from './certs.svelte';
  import { posterUrl, thumbBackground } from './cinemeta';
  import { beginDwell, dwellLeft, dwellSpent, haltDwell, runDwell } from './dwell';
  import { numeralSlide } from './focus-rules';
  import { focusByPress, focusState, focusable, isFocused, newFocusId, railScrollerOf, settledFocus, tapVerdict, watchOpenTile } from './focus.svelte';
  import { titleHref } from './nav.svelte';
  import { onVolumeIntent } from './volume-intent';
  import { lightDown, lightUp } from './spotlight.svelte';
  import { stageAttach, stageLoad, stagePlayer, stagePortal, stageRelease, tileStage } from './tile-stage.svelte';
  import {
    TILE_CARD_STILL_MS,
    TILE_EXPAND_MS,
    TILE_PRELOAD_MS,
    TILE_TRAILER_MAX_MS,
    autoplayAllowed,
    claimTrailer,
    holdsTrailer,
    releaseTrailer,
    spendAutoTrailer,
    tileExpandDue,
    watchConditions
  } from './trailer.svelte';

  interface Props {
    /** Omitted only when `ghost` is set. */
    item?: CatalogItem | undefined;
    /** 0–1. Omit for no bar. */
    progress?: number | undefined;
    eager?: boolean;
    /**
     * A CARD-SHAPED HOLE, for a rail that has not been answered yet.
     *
     * It is this component and not a copy of it in the caller, because the whole
     * point of a placeholder is that it is exactly the size of the thing it
     * stands in for — and only the card knows how tall a two-line title plus a
     * year comes to. A rail whose skeleton is 32 px shorter than its cards is a
     * measured layout shift on every screen it appears on.
     */
    ghost?: boolean;
    /**
     * 1-based position in a ranked row ("Top 10 this week"). Renders the
     * oversized outlined numeral Netflix uses. There is no rank field in the
     * catalogue — the caller passes the array index, so the ROW has to be
     * labelled by what it actually is.
     */
    rank?: number | undefined;
    /**
     * WHETHER THE KIND TAG ("Series") MAY BE DRAWN, decided by the CONTEXT and
     * not by sniffing rail titles for the word "series".
     *
     * The tag answers one question — "is this a film or a show?" — and a rail
     * that is single-kind BY CONSTRUCTION (a kind-scoped catalogue query, a
     * genre rail on /shows or /movies) has already answered it: a "Popular
     * series" rail wearing ten Series badges is saying the same thing eleven
     * times (the owner's 2026-08-16 note). A rail that genuinely pools kinds —
     * search results, the award rails, a mixed saved shelf — keeps the tag,
     * because there the answer is per tile.
     *
     * Defaults to true so a context that has NOT declared itself single-kind
     * stays honest. Callers that are single-kind by construction pass false;
     * `LazyRail` does it for every rail it draws, since its query is always
     * kind-scoped.
     */
    tagKind?: boolean;
    /**
     * THE ONE AWARD CORNER TAG, AND ONLY AN AWARD RAIL PASSES IT (the owner,
     * 2026-08-17: "we only show these tags for rails that are specifically
     * about awards, and we just show that award"). A "Golden Globe winners"
     * rail passes the Globe prize each tile actually won; every other surface
     * leaves this null and the corner belongs to the rating accolade, exactly
     * as it did before awards existed. The title's FULL prize list is not this
     * tag's job — it lives in the details strip (SpotlightStrip), next to the
     * year and rating, for every tile on every page.
     */
    awardBadge?: AwardId | null;
  }

  let { item, progress, eager = false, ghost = false, rank, tagKind = true, awardBadge = null }: Props = $props();

  /*
   * The numeral's cap height, read off the rail rather than guessed: the rail
   * declares --rank-size at each breakpoint (docs/DESIGN-LANGUAGE.md §5 — 74 on
   * a phone, 96 on a desktop, 128 on a television) and the stroke must not fall
   * under 2px or it disappears at distance. Read once on mount and on resize;
   * a CSS variable cannot be handed to an SVG attribute any other way.
   */
  let rankSize = $state(96);
  $effect(() => {
    if (rank === undefined) return;
    const el = node;
    if (!el || typeof getComputedStyle !== 'function') return;
    const read = (): void => {
      const raw = getComputedStyle(el).getPropertyValue('--rank-size').trim();
      const n = Number.parseFloat(raw);
      if (Number.isFinite(n) && n > 0) rankSize = n;
    };
    read();
    if (typeof ResizeObserver !== 'function') return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  });

  /*
   * HOW FAR THE NUMERAL MAY SLIDE, measured against the glass at every still
   * frame the tile is focused for.
   *
   * The slide used to be a fixed CSS distance (reveal 82% of the numeral,
   * whatever that took), and a fixed distance cannot know that focus lands on
   * tiles parked at the rail's left snap position — 14px from a phone's edge —
   * where a two-digit numeral's 48px slide put half the "10" off the screen
   * (`numeralSlide` in focus-rules.ts has the measurements). So the distance
   * is computed from where the numeral actually IS: its width and peek read
   * off the layout (margin-right is `peek − width`, so both come from two
   * resolved values, no ratio duplicated), and its rest position recovered by
   * subtracting the in-flight transform.
   *
   * MEASURED AT SETTLES, NOT PER SCROLL EVENT. This used to keep its own
   * capture scroll listener re-measuring every frame the tile stayed focused,
   * which put a `getComputedStyle` + rect read on the hot scroll path — and
   * every movement that could stale the clamp (an open card re-snapping the
   * rail, a thumb drag, a resize) raises `focusState.moving` and re-settles,
   * so re-running this effect at the settle covers exactly the same cases
   * with zero per-frame work. Mid-motion the numeral holds its last clamp;
   * the clamp's promise is about where it RESTS.
   */
  let rankSlide = $state(0);

  $effect(() => {
    if (rank === undefined || !focused || showTrailer) return;
    if (focusState.moving) return; // re-runs at the settle, measured fresh
    const el = node;
    if (!el || typeof getComputedStyle !== 'function' || typeof window === 'undefined') return;
    const svg = el.querySelector(':scope > .rank');
    if (!(svg instanceof SVGSVGElement)) return;

    const cs = getComputedStyle(svg);
    const rect = svg.getBoundingClientRect();
    // Translation does not change a rect's width, so this is `--rank-w`.
    const width = rect.width;
    if (width <= 0) return;
    // margin-right is `calc(--rank-peek − --rank-w)`, resolved to px here.
    const peek = width + (Number.parseFloat(cs.marginRight) || 0);
    // The rect includes any slide already in flight; subtract it to get the
    // numeral's layout position, which is what the clamp is about.
    const m = /matrix\(([^)]+)\)/.exec(cs.transform);
    const shifted = m ? Number.parseFloat(m[1]!.split(',')[4] ?? '0') || 0 : 0;
    rankSlide = numeralSlide({ width, peek, restLeft: rect.left - shifted });
  });

  /*
   * An accolade, from the only quality signal the catalogue actually carries.
   * There is no awards data in Cinemeta — no Oscars, no festival wins — so this
   * is honest about what it is: a rating badge, shown only when the rating is
   * high enough to mean something. Inventing a trophy from a number we do not
   * have would be a lie drawn in a nice icon.
   */
  /*
   * The corner tag on an AWARD RAIL is that rail's prize (`awardBadge`, passed
   * by AwardRail and nobody else); everywhere else the corner is the accolade's.
   * The two never share the corner — on an award rail the prize IS the row's
   * premise and repeating the rating next to it would be two "this is good"
   * claims in one 6px gap.
   */
  const award = $derived(awardBadge !== null ? AWARDS[awardBadge] : null);
  /* The age chip, drawn ONLY while the tile is focused — at rest a rail of
     twelve chips is noise; in focus it sits with the year, where the owner
     asked for it. `certOf` answers null until /certs.json lands and for every
     unrated title, and nothing is drawn from a null. */
  const cert = $derived(certOf(item?.id));

  const accolade = $derived.by(() => {
    const raw = Number.parseFloat(item?.imdbRating ?? '');
    if (!Number.isFinite(raw)) return null;
    if (raw >= 8.5) return { label: 'Top rated', value: raw.toFixed(1) };
    if (raw >= 8.0) return { label: 'Highly rated', value: raw.toFixed(1) };
    return null;
  });

  /**
   * A CARD ASKS FOR THE SIZE A CARD IS DRAWN AT.
   *
   * `cinemeta.ts` upgrades every metahub poster from `small` to `medium` on the
   * way in, which is right for the big plate on a title page and wrong here.
   * Measured: `poster/small` is 300×450 and 20–42 KB; `poster/medium` is 500×741
   * and 74–110 KB. This card is 136–195 CSS px wide (the owner's 2026-08 +25%),
   * so a 2× screen now wants up to 390 device pixels and `small` covers it at a
   * 1.3× upscale — mild on a photographic poster, and still chosen, because
   * `medium` is 2.7× the bytes for that last third of a pixel: on a twelve-rail
   * Home that is the difference between 14 MB of artwork and 4 MB. If the cards
   * ever grow again, this is the line to revisit (srcset, not a bigger default).
   *
   * Anything that is NOT a metahub URL is left exactly as it came, the same way
   * `thumbBackground()` treats a backdrop.
   */
  const art = $derived.by(() => {
    if (!item) return '';
    const given = item.poster;
    if (!given) return posterUrl(item.id, 'small');
    return given.includes('metahub.space') ? given.replace('/poster/medium/', '/poster/small/') : given;
  });

  const href = $derived(item ? titleHref(item.kind, item.id) : '');
  const percent = $derived(progress === undefined ? 0 : Math.min(100, Math.max(0, progress * 100)));

  /* ── Focus, and the trailer that follows it ─────────────────────────────── */

  /**
   * PER INSTANCE, not per title. The same film is legitimately in two rails at
   * once, and two cards claiming one key would fight over the trailer and both
   * draw themselves lifted.
   */
  const uid = newFocusId('tile');
  const focused = $derived(item !== undefined && isFocused(uid));
  /**
   * FOCUSED, AS OF THE LAST STILL FRAME. The ring and the lift ride `focused`
   * — live, mid-fling, the owner's favourite part. Everything that costs
   * LAYOUT rides `chosen` instead: the details strip's reveal (keyed on
   * `.poster.chosen` — SpotlightStrip.svelte), the strip's content
   * (`lightUp`), the cert chip joining the caption. While the screen is quiet
   * the two are the same answer in the same frame; during a fling `chosen`
   * holds, so a fling down Home is class toggles and transforms only, and the
   * strip hands over ONCE at the settle instead of twice per rail passed.
   * That difference is most of docs/PERF-SCROLL.md.
   */
  const chosen = $derived(item !== undefined && settledFocus.id === uid);
  const trailerId = $derived(item?.trailers?.[0] ?? null);
  const alternates = $derived(item?.trailers?.slice(1) ?? []);

  /*
   * Publish what this card is, while it is the SETTLED focused one, so the
   * rail can write the details under the row. Keyed on `chosen`, not
   * `focused`: the strip only reveals for the settled answer, so publishing
   * per live handover would re-render the open strip's text a dozen times
   * during one fling for words nobody can read mid-motion. Cleared on
   * teardown as well as on blur — a card that is destroyed mid-focus (a rail
   * re-rendering under it) would otherwise leave its title under a row it no
   * longer belongs to.
   */
  $effect(() => {
    if (chosen && item) {
      lightUp(uid, item, node?.closest('.strip-host') ?? null);
      return () => lightDown(uid);
    }
    lightDown(uid);
  });

  watchConditions();

  let node = $state<HTMLElement | null>(null);
  let linkEl = $state<HTMLAnchorElement | null>(null);
  let volEl = $state<HTMLElement | null>(null);

  /**
   * THE CHOSEN CARD DESCRIBES ITSELF THROUGH ITS STRIP. The details strip
   * (SpotlightStrip.svelte) publishes per-instance ids for its meta line and
   * blurb; the settled-focused card points `aria-describedby` at the ones in
   * its own `strip-host`, so a screen reader hears the description right
   * after the tile's label — no live region, nothing said twice.
   */
  let stripDesc = $state<string | null>(null);
  $effect(() => {
    if (!chosen || node === null) {
      stripDesc = null;
      return;
    }
    const base = node
      .closest('.strip-host')
      ?.querySelector('[data-strip-base]')
      ?.getAttribute('data-strip-base');
    stripDesc = base ? `${base}-meta${item?.description ? ` ${base}-blurb` : ''}` : null;
  });

  /**
   * TAB REACHES THE MUTE BUTTON, AND COMES BACK.
   *
   * While the video shows the button rides the body-level stage host
   * (`overVideo`), so raw DOM order would put it at the tail of the whole
   * document — a keyboard user could never sensibly reach it. Forward-Tab
   * from the open card's link hands it the focus instead; from the button,
   * Shift+Tab and Escape hand it straight back, and forward-Tab refocuses
   * the link first WITHOUT cancelling the key, so sequential navigation
   * carries on from the card to its natural next stop. The card never
   * collapses across the trip — focusOut (focus.svelte.ts) counts the stage
   * host as the focused card's own ground. `data-dpad-skip` still holds: a
   * remote never sends Tab, and the card stays ONE stop there.
   */
  function onLinkKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return;
    const btn = videoOn ? volEl?.querySelector('button') : null;
    if (!btn) return;
    event.preventDefault();
    btn.focus();
  }

  function onVolKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      linkEl?.focus();
      return;
    }
    if (event.key !== 'Tab') return;
    if (event.shiftKey) {
      event.preventDefault();
      linkEl?.focus();
      return;
    }
    linkEl?.focus(); // the un-cancelled Tab now advances FROM the card
  }

  /**
   * ONLY WHERE THERE IS ROOM TO GROW.
   *
   * A focused tile expands into a 16:9 card, which a rail can absorb — it is a
   * horizontal scroller, so its neighbours slide right and the page never
   * gains a pixel of width. A wrapping grid cannot: widening one cell reflows
   * every row after it. So the rail declares `--tile-open` (the width a 16:9
   * card comes to at this breakpoint) and a tile that cannot see one keeps its
   * poster shape and starts nothing. One contract, declared by the container
   * that has to live with the consequences.
   */
  function roomToOpen(): boolean {
    const el = node;
    if (!el || typeof getComputedStyle !== 'function') return false;
    return getComputedStyle(el).getPropertyValue('--tile-open').trim() !== '';
  }

  /**
   * The dwell. Focus alone is not a request for a trailer — running a pointer
   * along a rail focuses a dozen cards on the way past, and each one is ~4.1 MB
   * (docs/TRAILERS.md § D1). Only focus that has LASTED counts.
   *
   * It is a stopwatch rather than a countdown (`dwell.ts`): it banks what it has
   * and pauses while the page is moving, instead of starting again from zero on
   * every scroll event. That is what makes a dwell reachable with a thumb.
   *
   * ONE STOPWATCH, TWO MARKS — the owner's 2026-08-19 choreography:
   *
   *   • `TILE_PRELOAD_MS` (3 s banked): take the one trailer claim and start
   *     the shared stage INVISIBLY (`tile-stage.svelte.ts` — muted, hidden,
   *     the branding wait burning down where nobody sees it). Nothing on the
   *     glass changes; ring and strip carry on.
   *   • `TILE_EXPAND_MS` (6 s banked): the gate opens — the card expands once
   *     the stage is provably ROLLING (`tileExpandDue`), shows its banner art
   *     for `TILE_CARD_STILL_MS`, and only then uncovers the moving,
   *     branding-free picture (`videoOn`). Not rolling at 6 s? The compact
   *     tile holds and expands whenever proof lands. Never going to play — no
   *     id, embedding disabled, autoplay refused? The tile NEVER expands: the
   *     expanded-still card no longer exists.
   *
   * `spent` is the replay brake: a trailer that ran to its end (or proved
   * unplayable) hands the card back to its compact self and does not start
   * again until the eye has actually left — the hero's fresh-beat rule, here.
   */
  let claimed = $state(false);
  /** Banked ms at the moment the expansion gate opened; 0 while it is shut. */
  let gateBanked = $state(0);
  let spent = $state(false);
  let dwell = beginDwell(TILE_EXPAND_MS);
  let dwellSubject = '';

  $effect(() => {
    const key = `${focused}|${trailerId ?? ''}`;
    const moving = focusState.moving;
    const may = focused && !spent && trailerId !== null && autoplayAllowed(true);
    if (key !== dwellSubject) {
      dwellSubject = key;
      dwell = beginDwell(TILE_EXPAND_MS);
    }
    // Both marks passed: the stopwatch has nothing left to time.
    const done = claimed && gateBanked > 0;
    if (!may || moving || done) {
      dwell = haltDwell(dwell, Date.now());
      return;
    }
    const id = trailerId;
    dwell = runDwell(dwell, Date.now());
    const now = Date.now();
    const timers: ReturnType<typeof setTimeout>[] = [];
    if (!claimed) {
      timers.push(
        setTimeout(
          () => {
            if (id === null || !roomToOpen()) return;
            claimTrailer(uid, id);
            spendAutoTrailer();
            stageLoad(uid, [id, ...alternates], openSize(), frameEl);
            claimed = true;
          },
          Math.max(0, TILE_PRELOAD_MS - dwellSpent(dwell, now))
        )
      );
    }
    if (gateBanked === 0) {
      timers.push(
        setTimeout(() => {
          gateBanked = dwellSpent(dwell, Date.now());
        }, dwellLeft(dwell, now))
      );
    }
    return () => {
      for (const t of timers) clearTimeout(t);
      dwell = haltDwell(dwell, Date.now());
    };
  });

  /** A finished (or hopeless) play is consumed for THIS look; leaving resets it. */
  $effect(() => {
    if (!focused && spent) spent = false;
  });

  /**
   * The ends of a play that are the STAGE's to report, not the focus's:
   * the trailer ran out (the end screen is the branding coming back — the
   * card closes over it), the verdict went terminal ('still-only': device
   * refusal or every id exhausted — the expansion never happens and holding
   * the claim would only silence the banner), or the claim was taken by
   * someone else (`holdsTrailer` is the slot's truth; the local flags follow
   * it). Dropping `claimed` runs the release effect's teardown below, which
   * is the ONE place the stage and the claim are actually given back.
   */
  $effect(() => {
    if (!claimed) return;
    if (!holdsTrailer(uid)) {
      claimed = false;
      gateBanked = 0;
      return;
    }
    if (tileStage.owner !== uid) return;
    if (tileStage.ended || tileStage.verdict === 'still-only') {
      spent = true;
      claimed = false;
      gateBanked = 0;
    }
  });

  /** The OPEN card's box: the frame's height (constant through the expansion —
   *  Rail.svelte's arithmetic) by 16:9. What the stage sizes its frame to, so
   *  YouTube picks the real quality rung even while the video is hidden. */
  function openSize(): { w: number; h: number } {
    const el = frameEl;
    const h = el ? Math.round(el.getBoundingClientRect().height) : 0;
    if (h <= 0) return { w: 416, h: 234 };
    return { w: Math.round((h * 16) / 9), h };
  }

  /*
   * The release (and, only if the bound is finite, the cap). Separate from the
   * dwell because the dwell's effect re-runs whenever the page starts or stops
   * moving, and a scroll must not tear down a trailer that is already playing.
   * The 30s tile cap is RETIRED (owner, 2026-08-17: a focused tile plays its
   * whole trailer) — TILE_TRAILER_MAX_MS is Infinity now, and the guard below
   * is load-bearing: setTimeout coerces Infinity to 0, so arming the timer
   * unconditionally would kill every tile trailer at birth.
   *
   * KEYED ON `chosen` — THE SETTLED FOCUS — NOT THE LIVE ONE, and this is the
   * half-tile fix (the owner, iPhone, deployed 4548aba: "it tends to land on
   * a half tile"). The live focus hands over MID-FLING (trackLive recomputes
   * per frame), and keying the release on it collapsed the open card while
   * the momentum was still running: the card gave back (--tile-open − --col)
   * ≈ 226px of rail width, every snap position right of it moved, and the
   * momentum's endpoint — chosen by the engine at finger-lift against the
   * WIDER rail, never re-chosen on iOS — landed 226 mod stride ≈ 78.5px off
   * the grid, a tile half in, half out on the left. Measured at 390×844 on
   * both engines; Chromium re-snaps after layout changes and rescued the
   * landing with a visible whole-stride hop, WebKit's snap cache is exactly
   * the iOS behaviour the owner filmed. So the card HOLDS ITS WIDTH through
   * the fling — geometry the endpoint was computed against stays true to the
   * landing — and gives it back at the settle, where the focus hand-over is
   * one step and the collapse is compensated (see the shut discipline below).
   * On a quiet screen `chosen` and `focused` flip in the same microtask, so
   * hover, keyboard, d-pad and tap tear down exactly as they always did.
   */
  $effect(() => {
    const key = `${chosen}|${trailerId ?? ''}`;
    void key;
    if (!claimed) return;
    const cap = Number.isFinite(TILE_TRAILER_MAX_MS)
      ? setTimeout(() => {
          claimed = false;
          gateBanked = 0;
          stageRelease(uid);
          releaseTrailer(uid);
        }, TILE_TRAILER_MAX_MS)
      : null;
    return () => {
      if (cap !== null) clearTimeout(cap);
      claimed = false;
      gateBanked = 0;
      stageRelease(uid);
      releaseTrailer(uid);
    };
  });

  /*
   * The slot has exactly one holder; if something else took it, this card
   * stops. And the EXPANSION IS THE REVEAL: the card grows only once the
   * stage's picture is moving and branding-free AND seven seconds of real
   * attention are banked (`tileExpandDue`) — before that the preload plays
   * hidden and the compact tile keeps its ring and strip; without it (no id,
   * embedding refused, a device that will not autoplay) the card never grows
   * at all. `ended` closes the card over YouTube's end screen, the same rule
   * at the other end of the video.
   */
  const showTrailer = $derived(
    claimed &&
      trailerId !== null &&
      holdsTrailer(uid) &&
      tileStage.owner === uid &&
      !tileStage.ended &&
      tileExpandDue(gateBanked, tileStage.verdict, tileStage.rolling)
  );

  /*
   * THE BANNER BEAT (the owner, 2026-08-19): the expanded card shows its
   * artwork for TILE_CARD_STILL_MS before the video is uncovered — the
   * expansion at 6 s is the invitation, the banner is the title card, and the
   * motion arrives two seconds later. The uncover also waits for the reveal
   * verdict's 'show', so a branding burn still mid-flight at the beat's end
   * simply extends the banner rather than ever showing YouTube's chrome.
   */
  let bannerDone = $state(false);
  $effect(() => {
    if (!showTrailer) {
      bannerDone = false;
      return;
    }
    const beat = setTimeout(() => (bannerDone = true), TILE_CARD_STILL_MS);
    return () => clearTimeout(beat);
  });

  /** The video is actually on the glass — attach, mute control, badges ride this. */
  const videoOn = $derived(showTrailer && bannerDone && tileStage.verdict === 'show');

  /** The card's artwork frame — what the stage overlay rides. */
  let frameEl = $state<HTMLElement | null>(null);

  /*
   * The overlay ride: from the first frame of the expansion the stage is
   * positioned over this card's frame and unhidden — the video is wiped onto
   * the glass by the growth itself (tile-stage.svelte.ts explains the wipe).
   * Torn down whenever the expansion ends, wherever it ends.
   */
  $effect(() => {
    // `videoOn`, not `showTrailer`: through the banner beat the stage stays
    // parked (hidden) and the expanded card shows its own artwork.
    if (!videoOn) return;
    const el = frameEl;
    if (!el) return;
    return stageAttach(uid, el);
  });

  /*
   * THE OPEN CARD IS NEVER CLIPPED. The settle nudge guarantees the RESTING
   * tile's visibility; the expansion is a width change with no scroll behind
   * it, so at a rail's end the growing card ran off the right edge and nothing
   * corrected it (the owner watched a trailer play half behind the phone's
   * edge). While this tile is the open one, `watchOpenTile` re-aims the same
   * settle arithmetic at every size change — growth, trailer reveal, resize —
   * and defers to the hand whenever a pointer is down. See focus.svelte.ts.
   */
  $effect(() => {
    if (!showTrailer) return;
    const el = node;
    if (!el) return;
    return watchOpenTile(el);
  });

  /*
   * ── THE SHUT DISCIPLINE: an off-glass collapse must not move the glass ──
   *
   * A card that collapses IN VIEW animates — the 240ms flex-basis ease is the
   * designed gesture, neighbours sliding back in — and nothing here touches
   * it. A card that collapses with its left edge already OFF the glass (the
   * settled focus moved on after a fling; see the release above) is a
   * different animal: shrinking content left of the scrollport slides every
   * on-glass pixel leftward by the width it gives back, ≈ 226px — one and a
   * half columns of teleport under a rail that has just come to rest, and the
   * source of the misaligned landing when it ran mid-momentum.
   *
   * So when the shrink would happen off-glass it is INSTANT (`.shut` kills
   * the transition — a collapse nobody can see has no business animating)
   * and PIXEL-COMPENSATED in the same breath, the `shed()` discipline: an
   * anchor right of the card is measured, the closed width is applied as an
   * inline basis, the anchor is measured again, and scrollLeft absorbs the
   * difference — identical pixels onto identical pixels, before anything
   * paints. The settle's `flushSync` (focus.svelte.ts) runs this pre-effect
   * before the nudge aims, so the one correction that remains measures
   * geometry that is done moving.
   *
   * EVERYTHING HAPPENS INSIDE THE ONE $effect.pre, deliberately. The first
   * shape of this split the work across a pre-effect (measure) and a post-
   * effect (compensate after render) — and the post-effect's dependency had
   * already flipped in the flush cycle that RAN the release teardown, one
   * cycle before the pre-effect saw the falling edge, so it never re-ran and
   * the stash was orphaned: `.shut` stuck, scrollLeft uncompensated, and the
   * settle nudge papered over it with a visible 227px roll (measured in the
   * settle harness). Cross-effect ordering is not a contract; a single
   * synchronous block is. The inline flex-basis makes the collapse real
   * before the render even commits — the render then removes `.open` to a
   * card that already has its closed width, and changes nothing.
   *
   * The anchor is the next sibling rather than the card itself because the
   * card's own left edge does not move in content coordinates — everything
   * to its right is what shifts, and it is what the viewer is looking at. A
   * card with no next sibling is the rail's last child, which cannot be
   * off-glass-left while the rail is scrolled to it; the guard is theory.
   */
  let wasOpen = false;

  $effect.pre(() => {
    const open = showTrailer;
    if (!wasOpen || open) {
      wasOpen = open;
      return;
    }
    wasOpen = false;
    const el = node;
    if (el === null || typeof getComputedStyle !== 'function') return;
    const scroller = railScrollerOf(el);
    if (scroller === null) return;
    if (el.getBoundingClientRect().left >= scroller.getBoundingClientRect().left) return;
    const anchor = el.nextElementSibling;
    if (anchor === null) return;
    // Imperative class and style, not directives: they must be in force for
    // the very style recalc the compensation reads, in this same task.
    el.classList.add('shut');
    const x0 = anchor.getBoundingClientRect().left;
    // The closed width, from the same token the closed rule uses — resolved
    // against the rail's own scope, so ranked columns come out ranked-sized.
    el.style.flexBasis = 'var(--col)';
    const x1 = anchor.getBoundingClientRect().left; // forces the shut layout
    scroller.scrollLeft += x1 - x0;
    const done = (): void => {
      el.style.removeProperty('flex-basis');
      el.classList.remove('shut');
    };
    // Next frame — after the render has removed `.open` — the inline basis
    // hands back to the closed rule's identical value and `.shut` retires.
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(done);
    else done();
  });

  /* ── Sound, while the trailer is actually showing ───────────────────────── */

  /**
   * The session's one player, reached through the stage — only while THIS card
   * is showing it (`showTrailer` implies decoded, revealed video: the control
   * can no longer appear over a still, because the still-in-an-open-card state
   * no longer exists). The actual unmute() happens synchronously inside the
   * click gesture, because WebKit honours it nowhere else.
   */
  const tilePlayer = $derived(videoOn ? stagePlayer() : null);
  /** Ours, not `handle.muted()`: a tile trailer always STARTS muted, and the
      cross-origin getter answers a fallback while the bridge is settling. */
  let soundOn = $state(false);

  $effect(() => {
    // A trailer that has gone (scrolled away, superseded, finished) takes its
    // sound state with it; the next one starts muted like every other.
    if (!showTrailer) soundOn = false;
  });

  function toggleTileSound(): void {
    const h = tilePlayer;
    if (!h) return;
    if (soundOn) {
      h.mute();
      soundOn = false;
    } else {
      h.unmute();
      soundOn = true;
    }
  }

  /*
   * VOLUME TOUCHED = "I WANT TO HEAR THIS" (volume-intent.ts). Subscribed only
   * while THIS tile holds the trailer claim and decoded video is actually on
   * screen — the same condition that draws the MuteButton — so the intent can
   * only ever reach the one trailer that is playing, and the subscription dies
   * with it: the NEXT trailer starts muted like every other. The unmute runs
   * synchronously inside the callback (the shell dispatches from the key's own
   * gesture; WebKit honours it nowhere else), and `soundOn` is the same state
   * the MuteButton mirrors, so the icon flips with the sound.
   */
  $effect(() => {
    if (!showTrailer) return;
    return onVolumeIntent(() => {
      const h = tilePlayer;
      if (!h) return;
      h.unmute();
      soundOn = true;
    });
  });

  /**
   * OVER-VIDEO CHROME RIDES IN THE STAGE. The stage overlay necessarily paints
   * above this card's whole stacking context (`tile-stage.svelte.ts`), so the
   * two things designed to sit ON the moving picture — the mute control, an
   * award rail's badge on its open card — are adopted into the overlay for
   * exactly as long as the video is showing, and put back where they came from
   * after. They keep their scoped classes, so their own styles follow them.
   */
  function overVideo(el: HTMLElement, active: boolean): { update(a: boolean): void; destroy(): void } {
    let un: (() => void) | null = null;
    let home: { parent: Node; next: Node | null } | null = null;
    const set = (a: boolean): void => {
      if (a && un === null) {
        home = { parent: el.parentNode as Node, next: el.nextSibling };
        un = stagePortal(el);
      } else if (!a && un !== null) {
        un();
        un = null;
        if (home !== null && home.parent.isConnected) home.parent.insertBefore(el, home.next);
        home = null;
      }
    };
    set(active);
    return {
      update: set,
      destroy: () => {
        if (un !== null) {
          un();
          un = null;
        }
      }
    };
  }

  /* ── A tap is a hover; a second tap is a click ──────────────────────────── */

  /**
   * ON TOUCH, THE FIRST TAP FOCUSES AND THE SECOND NAVIGATES.
   *
   * With a mouse the hover already focused the card, so a click has always
   * meant "open it". A thumb has no hover, and without this every tap while
   * reading a rail — including the tap that stops a momentum scroll — was a
   * page navigation. The decision is per EVENT (`tapVerdict`,
   * `focus-rules.ts`): an iPad with a trackpad clicks like a mouse and touches
   * like a phone, in the same afternoon. Keyboard and d-pad activation carries
   * `detail === 0` and is never intercepted.
   *
   * The gesture's beginning is remembered at pointerdown because the verdict
   * needs two things as they were THEN: whether this card was focused before
   * the finger arrived, and what kind of pointer began it. WebKit's `click`
   * for a tap is synthesised from the compatibility MOUSE events and claims
   * `pointerType: 'mouse'` (measured — see `tapVerdict`'s header), so the
   * pointerdown is the only honest witness and `tapVerdict` trusts it first.
   *
   * The note is SAME-GESTURE ONLY, and two rules make it so: a pointercancel
   * (the tap became a scroll) erases it, and the click that reads it consumes
   * it — one pointerdown backs at most one click, so a keyboard Enter a second
   * later cannot inherit a finger's evidence. Correlation is by TIME, not by
   * element: after a micro-scroll the click's target can differ from the
   * pointerdown's, and both still belong to the one gesture.
   */
  let downType: string | null = null;
  let downFocused = false;
  let downAt = 0;

  function noteDown(event: PointerEvent): void {
    downType = event.pointerType || null;
    downFocused = focused;
    downAt = Date.now();
  }

  function cancelDown(): void {
    downAt = 0;
  }

  /**
   * An action rather than an `onpointerdown` attribute, because this is
   * bookkeeping about a gesture, not an interaction of its own — the div stays
   * exactly what it is to the accessibility tree, and the a11y rule that would
   * demand a role for a handler it can see is right to demand one there.
   */
  function watchGesture(el: HTMLElement): { destroy(): void } {
    el.addEventListener('pointerdown', noteDown);
    el.addEventListener('pointercancel', cancelDown);
    return {
      destroy: () => {
        el.removeEventListener('pointerdown', noteDown);
        el.removeEventListener('pointercancel', cancelDown);
      }
    };
  }

  function onLinkClick(event: MouseEvent): void {
    // A click with no gesture behind it (keyboard Enter, a synthetic .click())
    // must not read a stale pointerdown from minutes ago.
    const fresh = downAt !== 0 && Date.now() - downAt < 1500;
    downAt = 0; // consumed: this gesture's evidence answers for this click only
    const verdict = tapVerdict({
      pointerType: (event as PointerEvent).pointerType,
      downPointerType: fresh ? downType : null,
      hoverNone:
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(hover: none)').matches,
      detail: event.detail,
      focused: fresh ? downFocused : focused,
      moving: focusState.moving
    });
    if (verdict === 'navigate') return;
    event.preventDefault();
    if (verdict === 'focus') focusByPress(uid);
  }
</script>

{#if ghost || !item}
  <div class="poster" aria-hidden="true">
    <div class="frame"><Skeleton ratio="2 / 3" radius="var(--radius)" /></div>
    <p class="name"><Skeleton width="84%" height="0.72em" radius="5px" /></p>
    <p class="year"><Skeleton width="38%" height="0.72em" radius="5px" /></p>
  </div>
{:else}
<div
  class="poster"
  class:focused
  class:chosen
  class:ranked={rank !== undefined}
  class:open={showTrailer}
  style:--rank-slide={rank !== undefined ? `${rankSlide}px` : null}
  aria-owns={videoOn && tilePlayer ? `${uid}-vol` : null}
  bind:this={node}
  use:focusable={{ id: uid }}
  use:watchGesture
>
  {#if rank !== undefined}
    <RankNumeral value={rank} size={rankSize} lit={focused} />
  {/if}
  <a
    class="link"
    {href}
    title={rank !== undefined ? `${rank}. ${item.name}` : item.name}
    aria-describedby={stripDesc}
    onclick={onLinkClick}
    onkeydown={onLinkKeydown}
    bind:this={linkEl}
  >
    <!--
      The video is NOT a child of this frame: it plays in the one persistent
      stage (`tile-stage.svelte.ts`), positioned over exactly this box while
      `showTrailer` holds — which is why the frame is bound. The poster
      artwork stays underneath the ride, so collapsing is the picture coming
      back rather than a hole while it re-decodes.
    -->
    <div class="frame" bind:this={frameEl}>
      <Art
        src={art}
        fallbacks={[posterUrl(item.id, 'small'), posterUrl(item.id, 'medium')]}
        alt="{item.name} poster"
        label={item.name}
        {eager}
      />
      {#if claimed || showTrailer}
        <!-- THE OPEN CARD'S BANNER IS LANDSCAPE ART (the owner, 2026-08-19):
             the 16:9 card wearing a stretched portrait poster read wrong — the
             banner beat shows the title-page backdrop instead, cover-filling
             the frame. Mounted from the preload (`claimed`) so the bytes are
             in before the 6-second expansion; visible only while open. -->
        <img
          class="banner"
          class:shown={showTrailer}
          src={thumbBackground(item.background, item.id)}
          alt=""
          aria-hidden="true"
        />
      {/if}
      {#if percent > 0}
        <!-- While the video shows, the hairline rides the stage overlay
             (`overVideo`, like the badge): "how far you are" belongs ON the
             picture, not buried under it. -->
        <div class="bar" aria-hidden="true" use:overVideo={videoOn}><i style:width="{percent}%"></i></div>
      {/if}
      {#if tagKind && item.kind === 'series' && !videoOn}
        <!-- Hidden while the trailer shows: the open card's strip already
             says Series, and a tag drowned under the video was a lie. -->
        <span class="kind">Series</span>
      {/if}
      {#if award}
        <!-- The rail's OWN prize, and only that one — never the title's other
             wins (those live in the details strip). `expanded={showTrailer}`:
             on the open card the badge spells the prize's precise name, on the
             card's own clock — the state is the card's, so the two cannot
             drift (AwardMark.svelte). While the video shows, the badge rides
             the stage overlay (`overVideo`) so it stays ON the picture. -->
        <span class="badge-tr" use:overVideo={videoOn}><AwardMark {award} expanded={showTrailer} /></span>
      {:else if accolade && !videoOn}
        <!-- Same rule as the kind tag: gone while the video shows rather than
             under it — the rating lives in the strip on an open card. -->
        <span class="accolade" title="{accolade.label} — IMDb {accolade.value}">
          <Icon name="star" size={11} />
          <b>{accolade.value}</b>
        </span>
      {/if}
    </div>
    <p class="name">{item.name}</p>
    <!-- The chip joins on `chosen`, not `focused`: inserting DOM into the
         caption of every tile a fling passes is layout work mid-scroll for a
         chip nobody can read mid-motion. At rest the two are the same tile. -->
    {#if item.year || (chosen && cert)}
      <p class="year">
        {#if item.year}<span>{item.year}</span>{/if}
        {#if chosen && cert}<CertChip {cert} />{/if}
      </p>
    {/if}
  </a>

  {#if videoOn && tilePlayer}
    <!--
      OUTSIDE THE LINK, because a button inside an anchor is invalid HTML that
      browsers resolve by guessing — and adopted into the stage overlay for
      its whole life (`overVideo`), because the video paints above this card's
      stacking context and a mute control must ride ON the picture.
      `data-dpad-skip` keeps the card ONE stop on a remote — the title page a
      press reaches has full controls of its own.
    -->
    <span class="vol" id="{uid}-vol" data-dpad-skip use:overVideo={true} bind:this={volEl}>
      <MuteButton muted={!soundOn} ontoggle={toggleTileSound} size={30} onkeydown={onVolKeydown} />
    </span>
  {/if}
</div>
{/if}

<style>
  .poster {
    position: relative;
    display: block;
    width: 100%;
    min-width: 0;
    /*
     * THE EXPANSION, AND WHY IT IS `flex-basis`.
     *
     * A rail is a flex row that declares `--tile-open`; a focused card takes
     * that width and its neighbours slide right inside the rail's own scroller,
     * so the row never reflows and the PAGE never gains a pixel of width. A
     * wrapping grid declares nothing — and `flex-basis` is inert on a grid item,
     * so the very same rule quietly does nothing there rather than reflowing
     * every row after the card. The container decides; the tile obeys.
     */
    transition: flex-basis 240ms var(--ease-out);
  }
  .poster.open {
    flex-basis: var(--tile-open);
  }
  /*
   * The OFF-GLASS collapse arrives, it does not animate — see the shut
   * discipline in the script. One frame only (`.shut` is removed on the next
   * rAF). The numeral is included so a ranked card lands whole: its peek
   * margin eases on the expansion's own clock, and a card that has just been
   * pixel-teleported shut must not spend 240ms rearranging its insides.
   * !important, because it must beat the reduced-motion blocks as well as
   * the base transitions, on a card nobody can see either way.
   * `:global(.shut)`: the class is added imperatively (classList, not a
   * class: directive — see the script), and the compiler prunes any scoped
   * selector it cannot see in the template.
   */
  .poster:global(.shut),
  .poster:global(.shut) :global(.rank) {
    transition: none !important;
  }
  .link {
    display: block;
  }

  /*
   * THE HEIGHT IS THE RAIL'S, NOT THE PICTURE'S. `--tile-h` is the height a
   * poster comes to at this breakpoint, and `--tile-open` is exactly
   * `--tile-h × 16/9` — so a card that grows into a 16:9 frame keeps the height
   * it already had, to the pixel, and the row cannot move vertically while it
   * happens. Off a rail there is no `--tile-h` and the artwork's own 2:3 decides,
   * exactly as before.
   */
  .frame {
    position: relative;
    height: var(--tile-h, auto);
    border-radius: var(--radius);
    overflow: hidden;
    background: var(--surface-2);
    /* Snappier: the lift is an acknowledgement, and an acknowledgement that
       takes a fifth of a second reads as lag when a thumb is moving through a
       row. --ease-out arrives rather than eases both ways. */
    transition:
      transform 130ms var(--ease-out),
      box-shadow 130ms var(--ease-out);
  }
  .frame > :global(.art) {
    /* Against an `auto` parent this resolves to `auto` and the ratio still
       decides, which is what keeps grid pages untouched. */
    height: 100%;
  }

  /*
   * THE FOCUSED TILE, and it is meant to be unmistakable: lifted off the shelf,
   * a little larger than its neighbours, inside a light rim.
   *
   * Focus is ONE idea with one owner (`focus.svelte.ts`) and two meanings —
   * under a pointer it is what the pointer is over or the keyboard has reached;
   * under a thumb it is whatever has been brought to the middle of the screen.
   * This rule does not know or care which; it draws whatever that one answer is.
   *
   * The card raises its own stacking context so the growth overlaps its
   * neighbours instead of being painted under the next one along.
   */
  .poster.focused {
    position: relative;
    z-index: 2;
  }
  /*
   * The ring is --focus-ring, not --accent: the brand colour and "where you
   * are" must not share a hue, or a row of accent badges competes with the
   * selection. The spread is a box-shadow rather than a border so it sits
   * OUTSIDE the frame and steals no artwork — a border would crop focused and
   * unfocused posters differently. The lift underneath carries a real offset
   * and blur; a zero-offset halo is decoration, not depth.
   */
  .banner {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
    pointer-events: none;
    /* Rides the structure clock: it fades in with the card's own growth. */
    transition: opacity 240ms var(--ease-out);
  }
  .banner.shown {
    opacity: 1;
  }

  .poster.focused .frame {
    transform: translateY(-6px) scale(1.05);
    box-shadow:
      0 18px 40px rgb(0 0 0 / 0.55),
      0 0 0 2px var(--focus-ring),
      0 0 26px -6px color-mix(in oklab, var(--focus-ring) 45%, transparent);
  }
  /* Across a room a 2px ring disappears into overscan and screen-door; the
     design language specifies 2.5px on TV. No media query can detect a
     television (an Android TV WebView reports pointer:coarse and hover:none
     exactly like a phone), so this hangs off tv.ts's attribute. */
  :global(html[data-tv='on']) .poster.focused .frame {
    box-shadow:
      0 18px 40px rgb(0 0 0 / 0.55),
      0 0 0 2.5px var(--focus-ring),
      0 0 30px -6px color-mix(in oklab, var(--focus-ring) 45%, transparent);
  }
  /*
   * An open card is already unmistakable — it is nearly three times the width of
   * its neighbours with a picture playing in it. The 5% growth was there to say
   * "this one"; keeping it as well would overhang the card either side of a
   * frame that is now doing the talking.
   *
   * The scale comes off on the SAME 240ms clock the width grows on: the two
   * are one gesture, and at the lift's own 130ms the shrink finished while the
   * card was still growing, which read as a stutter inside the expansion.
   */
  .poster.open .frame {
    transform: translateY(-6px);
    transition-duration: 240ms;
  }
  .link:active .frame {
    transform: translateY(0);
  }
  /*
   * NO SECOND RING. The focused treatment above already answers keyboard reach
   * — `focusin` routes through the one focus owner (`focus.svelte.ts`), so a
   * tile the keyboard lands on is drawn with the same rim as one the pointer
   * chose. A separate :focus-visible outline meant TWO rings whenever DOM
   * focus and the app's focus disagreed (park the keyboard on a card, then
   * hover another: both wore one), and exactly one thing on a browse screen
   * may claim "you are here".
   */
  .link:focus-visible {
    outline: none;
  }

  /* Somebody who asked for less motion still gets the rim — the point of the
     treatment is knowing WHICH tile, and that survives without the movement. */
  @media (prefers-reduced-motion: reduce) {
    .poster.focused .frame,
    .poster.open .frame {
      transform: none;
    }
    .frame {
      transition: box-shadow 220ms var(--ease);
    }
    /* Nothing expands here anyway — reduced motion refuses every automatic
       trailer — but if one is ever started deliberately it arrives, it does not
       animate. */
    .poster {
      transition: none;
    }
    /* The numeral still steps out (knowing WHICH number survives without the
       movement) — it just arrives instead of sliding. */
    .poster.ranked :global(.rank) {
      transition: none;
    }
  }

  .bar {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 3px;
    background: rgb(0 0 0 / 0.55);
  }
  .bar i {
    display: block;
    height: 100%;
    background: var(--accent);
  }

  /*
   * ── The accolade ────────────────────────────────────────────────────────
   * Top RIGHT, because the Series flag owns the top left and two badges in one
   * corner is how a card stops being scannable. The star is --accent-warm and
   * not --warn: --good/--warn/--bad are verdicts in this system and a rating is
   * not a verdict on the app's own state.
   */
  .badge-tr {
    position: absolute;
    top: 6px;
    right: 6px;
    display: block;
  }
  .accolade {
    position: absolute;
    top: 6px;
    right: 6px;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 2px 6px 2px 4px;
    border-radius: 5px;
    background: color-mix(in oklab, var(--bg) 74%, transparent);
    backdrop-filter: blur(8px) saturate(1.3);
    box-shadow: var(--glass-shine);
    color: var(--accent-warm);
    line-height: 1;
    pointer-events: none;
  }
  .accolade b {
    font-size: var(--text-2xs);
    font-weight: var(--weight-bold);
    color: var(--text);
    letter-spacing: 0.01em;
  }

  /*
   * ── Ranked rows ─────────────────────────────────────────────────────────
   * Stroke-only type sitting beside the artwork, overlapped by it — the poster
   * occludes part of the digit, which is what makes the pair read as one
   * object rather than a number next to a picture. `tabular-nums` keeps 1 and
   * 11 optically aligned. The stroke never goes under 2px: thinner than that
   * and it disappears at TV distance, which is where these rows are read from.
   */
  /*
   * THE NUMERAL AND THE POSTER ARE ONE OBJECT, AND THE POSTER'S SIZE IS
   * NON-NEGOTIABLE.
   *
   * The numeral occupies exactly `--rank-peek` of layout whatever its digit
   * count: its own width is `--rank-w` (the SVG declares it — see
   * RankNumeral.svelte) and the negative margin hands back everything beyond
   * the peek, so "10" simply sits FURTHER BEHIND its poster than "9" does.
   * The old layout kept ~34% of --rank-size as a fixed overlap, which made a
   * two-digit numeral eat 52px of its own tile — the rank-10 card was visibly
   * smaller than its neighbours, in the one row that claims to be a line-up.
   *
   * WHEN THE TILE TAKES FOCUS, THE NUMERAL SLIDES OUT from behind the artwork
   * until ~82% of it shows, on the same 130ms var(--ease-out) the tile lifts
   * with, so digit and card read as one movement. The focused poster is
   * z-index 2 in its row, so the emerging digit paints over the neighbouring
   * card it slides across — deliberate: it is the focused card's own flourish.
   *
   * THE DISTANCE IS `--rank-slide`, WRITTEN BY THE SCRIPT, NOT A FIXED CALC.
   * A fixed `0.82·W − peek` cannot know where the glass is, and the focused
   * tile routinely sits at the rail's left snap position 14px from it — from
   * there a two-digit slide of 48px pushed half the "10" off an iPhone's
   * screen (the owner's QA find; the arithmetic and the measurements are in
   * `numeralSlide`, focus-rules.ts). The script measures the room and clamps;
   * the fallback is 0 so a numeral never overshoots while unmeasured.
   *
   * `z-index` and not source order: the poster has to paint OVER the digit.
   */
  .poster.ranked {
    display: flex;
    align-items: flex-end;
  }
  .poster.ranked :global(.rank) {
    margin-right: calc(var(--rank-peek, 28px) - var(--rank-w, 58px));
    margin-bottom: calc(var(--rank-size, 96px) * 0.06);
    transition:
      transform 130ms var(--ease-out),
      margin-right 240ms var(--ease-out),
      opacity 130ms var(--ease-out);
  }
  .poster.ranked.focused :global(.rank) {
    transform: translateX(calc(-1 * var(--rank-slide, 0px)));
  }
  .poster.ranked .link {
    flex: 1;
    min-width: 0;
    position: relative;
    z-index: 1;
  }
  /*
   * A card that has opened is wide enough to say it on its own — and the open
   * card must be a TRUE 16:9, so the numeral gives up its peek too (the
   * margin collapse runs on the expansion's own 240ms clock; it is the same
   * reflow the flex-basis change is already paying for).
   */
  .poster.ranked.open :global(.rank) {
    opacity: 0;
    margin-right: calc(0px - var(--rank-w, 58px));
    transform: none;
  }

  .kind {
    position: absolute;
    top: 6px;
    left: 6px;
    padding: 2px 6px;
    border-radius: 5px;
    background: rgb(8 10 15 / 0.78);
    color: var(--text-dim);
    /* --text-2xs is the floor for anything carrying words; this badge was under
       it at 10.08px. */
    font-size: var(--text-2xs);
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  /*
   * The trailer's volume, bottom-right of the ARTWORK (the frame ends at
   * --tile-h; below that is caption). Only rendered while video is actually
   * showing, so it fades in with the reveal rather than popping.
   */
  .vol {
    position: absolute;
    right: 8px;
    /* 30px of button + 8px in from the artwork's bottom edge — WHICH IS
       LIFTED: an open card's frame rides at translateY(-6px), and a sibling
       does not inherit a transform, so the 6 is added here. */
    top: calc(var(--tile-h, 100%) - 44px);
    z-index: 2;
    animation: vol-in 130ms var(--ease-out);
  }
  /*
   * The same control, ADOPTED INTO THE STAGE OVERLAY (`overVideo` — the video
   * paints above the card, so its chrome rides with it). The host IS the
   * lifted frame's box, so the 8px comes straight off its bottom edge; the
   * host swallows pointer events (it is scenery), so the control opts back in.
   */
  :global(.jt-tile-stage) .vol {
    top: auto;
    bottom: 8px;
    pointer-events: auto;
  }
  @keyframes vol-in {
    from {
      opacity: 0;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .vol {
      animation: none;
    }
  }

  .name {
    margin-top: 7px;
    /* Two lines' worth, always: otherwise a one-line title lifts its year and the
       row's baselines go ragged. */
    min-height: 2.1em;
    font-size: 0.82rem;
    font-weight: 500;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .year {
    /* Stated, so the placeholder's box and a real year's line box are the same
       height without the placeholder having to know what a year looks like. */
    min-height: 1.4em;
    color: var(--text-faint);
    font-size: 0.72rem;
    line-height: 1.4;
    /* The focused tile's age chip sits in this line; flex keeps year and chip
       on one baseline, and the chip is sized below to fit INSIDE the stated
       1.4em so its appearance on focus never shifts the caption. */
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .year :global(.cert) {
    font-size: 0.6rem;
    padding: 0 4px;
    line-height: 1.25;
  }

  @media (max-width: 767px) {
    .poster {
      /* Shared by the artwork frame and its loading placeholder. */
      --radius: 6px;
    }
    .name {
      margin-top: 5px;
    }
    .kind {
      top: 4px;
      left: 4px;
      padding-inline: 4px;
    }
    .accolade {
      top: auto;
      bottom: 6px;
      right: 4px;
      gap: 2px;
      padding-inline: 3px;
    }
  }

  /*
   * CAPTIONS AT TEN FEET. 13.1/11.5px CSS under a tile is ~20/17 real pixels
   * on a 1080p panel — under the 10-foot floor app.css's TV token tier holds
   * everything else to (the owner: squinty from the sofa). TV only; the
   * min-heights above are in em, so the reserved caption space scales with
   * the type and the row's baselines stay ragged-free.
   */
  :global(html[data-tv='on']) .name {
    font-size: 0.95rem;
  }
  :global(html[data-tv='on']) .year {
    font-size: 0.82rem;
  }
</style>
